/**
 * Workshop Service
 * API calls for workshop management and dynamic car queries
 */

import api from './api';
import { Workshop } from '../types/workshop';

export interface ExternalCarResponse {
    id: number;
    number: string;
    tank: string;
    created_at: string | null;
    updated_at: string | null;
    deleted_at: string | null;
    name_driver: string | null;
    phone_driver: string | null;
    plate: string | null;
    oil_chain: string | null;
    photo: number | null;
    next_oil_chain: string | null;
    deliver_date: string | null;
    receive_date: string | null;
    delivered_to: string | null;
    received_to: string | null;
    deliv_receiv_status: string | null;
    deliver_tools: string | null;
    receive_tools: string | null;
    status: 'available' | 'working' | 'maintenance' | 'no_drive';
    status_info: string | null;
    in_pusher: number | null;
}

export interface CarCountResponse {
    workshop_id: number;
    total_cars: number;
    status: string | null;
}

class WorkshopService {
    /**
     * Get all workshops accessible by current user
     */
    async getWorkshops(): Promise<Workshop[]> {
        return await api.get<Workshop[]>('/workshops/');
    }

    /**
     * Get specific workshop by ID
     */
    async getWorkshop(id: number): Promise<Workshop> {
        return await api.get<Workshop>(`/workshops/${id}`);
    }

    /**
     * Get cars from a specific workshop
     */
    async getCars(
        workshopId: number,
        params?: {
            skip?: number;
            limit?: number;
            status?: string;
            search?: string;
        }
    ): Promise<ExternalCarResponse[]> {
        return await api.get<ExternalCarResponse[]>(
            `/workshops/${workshopId}/cars/`,
            params
        );
    }

    /**
     * Search for a car by number in workshop
     */
    async searchCarByNumber(
        workshopId: number,
        number: string
    ): Promise<ExternalCarResponse> {
        return await api.get<ExternalCarResponse>(
            `/workshops/${workshopId}/cars/search/${number}`
        );
    }

    /**
     * Get available cars from workshop
     */
    async getAvailableCars(
        workshopId: number,
        params?: {
            skip?: number;
            limit?: number;
        }
    ): Promise<ExternalCarResponse[]> {
        return await api.get<ExternalCarResponse[]>(
            `/workshops/${workshopId}/cars/available`,
            params
        );
    }

    /**
     * Count total cars in workshop
     */
    async countCars(
        workshopId: number,
        status?: string
    ): Promise<CarCountResponse> {
        return await api.get<CarCountResponse>(
            `/workshops/${workshopId}/cars/count`,
            status ? { status } : undefined
        );
    }

    /**
     * Get specific car by ID from workshop
     */
    async getCar(workshopId: number, carId: number): Promise<ExternalCarResponse> {
        return await api.get<ExternalCarResponse>(
            `/workshops/${workshopId}/cars/${carId}`
        );
    }
}

export default new WorkshopService();
